import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // 2. Import useParams and useNavigate
import { useAuth } from '../hooks/useAuth';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import SurveyCreatorWidget from '../components/ui/SurveyCreator';

import API_CONFIG from '../config/api';
const BASE_URL = API_CONFIG.BASE_URL;

function EvaaSurveyEditorPage() {
	const { surveyId } = useParams();
	const navigate = useNavigate(); // Initialize useNavigate hook
	const { isAuthenticated } = useAuth();
	const { authenticatedFetch } = useAuthenticatedApi();
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [surveyModel, setSurveyModel] = useState([]);

	const getSurvey = async () => {
		if (!isAuthenticated) {
			setIsLoading(false);
			return;
		}
		setIsLoading(true);
		setError(null); // Clear previous errors
		try {
			const result = await authenticatedFetch(BASE_URL + '/surveys/survey-by-id/' + surveyId, {}, "GET");

			if (result.status === 404) {
				// Redirect to 404 page with a custom message
				navigate('/404', { state: { message: `Survey with ID "${surveyId}" not found in database.` } });
				return;
			}

			if (!result.ok) {
				// Handle other non-2xx status codes (e.g., 500, 401, 403)
				let errorMessage = `Failed to fetch survey: Server responded with status ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.message) {
						errorMessage = errorBody.message;
					}
				} catch (jsonError) {
					// Ignore if response body isn't JSON
				}
				console.error("EvaaSurveyEditorPage API Error:", errorMessage, result);
				setError(errorMessage);
				setSurveyModel([]); // Clear model on error
				return; // Stop further execution
			}

			const datas = await result.json();

			if (datas) {
				setSurveyModel(datas);
			} else {
				console.error("API returned empty or invalid data structure:", datas);
				setError("Survey data is empty or malformed.");
				setSurveyModel([]); // Set empty list on unexpected response
			}
		} catch (err) {
			console.error("Error fetching survey list:", err);
			setError("Failed to load surveys due to network or unexpected error. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (isAuthenticated) {
			getSurvey();
		} else if (isAuthenticated === false) {
			setSurveyModel([]);
			setIsLoading(false);
		}
	}, [surveyId, isAuthenticated]); // Added navigate as a dependency is generally recommended for exhaustive deps, but functionally not strictly needed for this specific use case as navigate never changes.

	if (isLoading) {
		return <div>Loading survey...</div>;
	}

	if (error) {
		return <div style={{ color: 'red' }}>Error: {error}</div>;
	}

	// Assuming surveyModel.survey contains the JSON data for the SurveyCreatorWidget
	// If surveyModel itself is the JSON data, just pass surveyModel
	return (
		<div>
			{surveyModel.survey ? (
				<SurveyCreatorWidget json={surveyModel.survey} json_theme={surveyModel.survey_theme} survey_id={surveyModel.survey_id} />
			) : (
				<div>No survey data to display.</div>
			)}
		</div>
	);
}

export default EvaaSurveyEditorPage;